import dayjs from 'dayjs'
import {
  ChatMessage,
  FormFieldSpec,
  MessagePart,
  OptionItem,
  PermissionGroupItem,
  QuestionPageSpec,
  UiResolution,
  UiStatus,
} from '../../AiAssistant/aiAssistantModel'

export type ChatSearchParams = {
  sessionId?: string
  userName?: string
  q?: string
  from?: string
  to?: string
  tenantGuid?: string
  myChats?: boolean
  page: number
  pageSize: number
}

export type ChatSearchResultItem = {
  session_id: string
  tenant_guid: string
  user_name: string
  model: string | null
  title: string | null
  /**
   * Which app the session started in. Null for sessions archived before the
   * API grew the column — those are not backfilled, so the gap is permanent.
   * Not narrowed to a union: the API owns the enum and can add a platform
   * without this build knowing, and an unlisted value must still render.
   */
  client: string | null
  started_at: string
  last_message_at: string
  message_count: number
  input_tokens: number
  output_tokens: number
  cost_usd: string
}

export type ChatSearchResponse = {
  results: ChatSearchResultItem[]
  total: number
  page: number
  page_size: number
}

export type SessionToolCall = {
  id: string
  type: 'function'
  function: { name: string; arguments: string }
  /** User-facing label the chat service adds when serving history; null when the tool has none. */
  title?: string | null
}

/** The visual elements the harness records in the message log. */
export type SessionUiKind =
  | 'form'
  | 'question_set'
  | 'option_select'
  | 'permission_request'
  // One record per consolidated approval card, not per row (chat#169).
  | 'permission_group_request'

export type SessionMessage = {
  role: 'user' | 'assistant' | 'tool' | 'system' | 'ui'
  content?: string
  tool_call_id?: string
  tool_calls?: SessionToolCall[]
  // role === 'tool' only, and only on rows written after chat#150. Absent on
  // everything archived before it — see `toolResultFailed`.
  is_error?: boolean
  // role === 'ui' only. `payload` is the SSE event the client originally
  // rendered, so a history replays exactly what the user saw.
  kind?: SessionUiKind
  // role === 'ui' with kind 'permission_group_request' only: a group is not one
  // call, so it is keyed by its own id rather than borrowing tool_call_id.
  group_id?: string
  payload?: Record<string, unknown>
  status?: UiStatus
  resolution?: UiResolution
}

export type ChatSessionDetail = ChatSearchResultItem & {
  system_prompt: string | null
  messages: SessionMessage[]
}

export function getDefaultSearchParams(now: dayjs.Dayjs = dayjs()): ChatSearchParams {
  return {
    from: now.subtract(7, 'day').startOf('day').toISOString(),
    to: now.endOf('day').toISOString(),
    page: 1,
    pageSize: 20,
  }
}

export function buildExportParams(params: ChatSearchParams): URLSearchParams {
  const query = new URLSearchParams()
  if (params.sessionId) {
    query.set('session_id', params.sessionId)
  }
  if (params.userName) {
    query.set('user_name', params.userName)
  }
  if (params.q) {
    query.set('q', params.q)
  }
  if (params.from) {
    query.set('from', params.from)
  }
  if (params.to) {
    query.set('to', params.to)
  }
  if (params.tenantGuid) {
    query.set('tenant_guid', params.tenantGuid)
  }
  return query
}

const CLIENT_LABELS: Record<string, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
  unknown: 'Unknown',
}

/**
 * Display label for a session's originating platform.
 *
 * Three cases are deliberately distinct. A known platform gets a cased label.
 * A platform this build hasn't heard of passes through verbatim rather than
 * collapsing to 'Unknown', which would make a newly-shipped platform
 * indistinguishable from a session that sent no header. Absent data renders as
 * a dash, matching how the Model column shows nothing to display.
 */
export function formatClient(client: string | null | undefined): string {
  if (!client) {
    return '—'
  }
  return CLIENT_LABELS[client] ?? client
}

function parseArguments(args: string): Record<string, unknown> {
  try {
    return JSON.parse(args)
  } catch {
    return {}
  }
}

/**
 * Rebuild the message part for a presented visual element, or `null` when the
 * record can't be rendered — an unrecognized `kind` (a newer harness recorded
 * something this build doesn't know) or a missing payload. Dropping is
 * deliberate: an unrenderable record must not break the rest of the history.
 *
 * The `status` and `resolution` ride along and mark the part as historical, so
 * it renders read-only.
 */
export function uiMessageToPart(msg: SessionMessage): MessagePart | null {
  const payload = msg.payload
  if (!payload) {
    return null
  }
  const resolved = { status: msg.status, resolution: msg.resolution ?? null }

  switch (msg.kind) {
    case 'form':
      return {
        type: 'form_request',
        form_id: payload.form_id as string,
        prompt: payload.prompt as string,
        submit_label: payload.submit_label as string,
        fields: payload.fields as FormFieldSpec[],
        ...resolved,
      }
    case 'question_set':
      return {
        type: 'question_set',
        question_set_id: payload.question_set_id as string,
        prompt: payload.prompt as string,
        pages: payload.pages as QuestionPageSpec[],
        ...resolved,
      }
    case 'option_select':
      return {
        type: 'option_select',
        prompt: payload.prompt as string,
        options: payload.options as OptionItem[],
        ...resolved,
      }
    case 'permission_request':
      return {
        type: 'permission_request',
        tool_call_id: payload.tool_call_id as string,
        tool_name: payload.tool_name as string,
        arguments: payload.arguments as Record<string, unknown>,
        summary: payload.summary as string | undefined,
        // The rich text the write was about to store. Shipped in #469 for the
        // live card; without it here, reopening a conversation loses the text
        // the user actually approved.
        body: payload.body as string | undefined,
        ...resolved,
      }
    case 'permission_group_request':
      // The row list and each row's final outcome, so a replay shows what
      // actually happened to the data — including a partial failure, where some
      // rows applied and the rest were abandoned.
      return {
        type: 'permission_group_request',
        group_id: payload.group_id as string,
        tool_name: payload.tool_name as string,
        count: payload.count as number,
        summary: payload.summary as string,
        items: payload.items as PermissionGroupItem[],
        ...resolved,
      }
    default:
      return null
  }
}

/**
 * Whether a replayed tool result should render as failed.
 *
 * Replay used to hardcode this to `false`, so every failed call came back green
 * and every retrospective review of production chats under-counted tool
 * failures (chat#150). The server now records `is_error`, and where a row has
 * it, it is authoritative.
 *
 * Older rows have no flag. Their only signal is the `Error: ` prefix that
 * `build_tool_result_message` puts on the content for the model to read, so the
 * prefix is the fallback rather than a guess: measured on staging, 1498
 * archived tool results carry no flag and 59 of them are failures across 16% of
 * sessions. Without this the whole existing archive stays green.
 */
export function toolResultFailed(msg: SessionMessage): boolean {
  if (typeof msg.is_error === 'boolean') {
    return msg.is_error
  }
  return (msg.content ?? '').startsWith('Error: ')
}

/**
 * Rebuild chat bubbles from an archived transcript.
 *
 * The archive holds one assistant message per model step, so a turn that
 * called tools over several steps spans several assistant messages. With
 * `mergeAssistantTurns` on, consecutive assistant messages join into one
 * bubble, the way live chat shows the whole turn; off, each gets its own.
 */
export function convertSessionMessages(
  messages: SessionMessage[],
  mergeAssistantTurns = false
): ChatMessage[] {
  const result: ChatMessage[] = []
  let msgIndex = 0

  for (const msg of messages) {
    if (msg.role === 'system') {
      continue
    }

    if (msg.role === 'tool') {
      // Attach tool result to the last assistant message
      const lastMsg = result[result.length - 1]
      if (lastMsg && lastMsg.role === 'assistant' && msg.tool_call_id) {
        lastMsg.parts.push({
          type: 'tool_result',
          tool_call_id: msg.tool_call_id,
          content: msg.content ?? '',
          is_error: toolResultFailed(msg),
        })
      }
      continue
    }

    if (msg.role === 'ui') {
      // Attach the presented element to the assistant turn that produced it,
      // the same bubble it appeared in live. Never push a message of its own:
      // the following tool results still need that assistant to be last.
      const lastMsg = result[result.length - 1]
      const part = uiMessageToPart(msg)
      if (part && lastMsg && lastMsg.role === 'assistant') {
        lastMsg.parts.push(part)
      }
      continue
    }

    const parts: MessagePart[] = []

    if (msg.content) {
      parts.push({ type: 'text', content: msg.content })
    }

    if (msg.tool_calls) {
      for (const tc of msg.tool_calls) {
        parts.push({
          type: 'tool_call',
          tool_call_id: tc.id,
          tool_name: tc.function.name,
          arguments: parseArguments(tc.function.arguments),
          tool_title: tc.title,
        })
      }
    }

    const lastMsg = result[result.length - 1]
    if (mergeAssistantTurns && msg.role === 'assistant' && lastMsg?.role === 'assistant') {
      lastMsg.parts.push(...parts)
      continue
    }

    result.push({
      id: `msg-${msgIndex++}`,
      role: msg.role as 'user' | 'assistant',
      parts,
    })
  }

  return result
}
